export default class Calendar {
	private locale: string

	constructor(locale: string) {
		this.locale = locale
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * this.formatDate(new Date('2026-06-18'), { day: 'numeric', month: 'short' }) // → 'Jun 18'
	 */
	private formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
		return date.toLocaleDateString(this.locale, options).replace(',', ' ')
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * calendar.formatDayMonthToLocale('2026-06-18') // → 'Jun 18'
	 */
	formatDayMonthToLocale(datetime: string | Date): string {
		const options: Intl.DateTimeFormatOptions = {
			day: 'numeric',
			month: 'short',
		}
		return this.formatDate(new Date(datetime), options)
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * calendar.formatDateToLocale('2026-06-18') // → 'Jun 18  2026'
	 */
	formatDateToLocale(datetime: string | Date): string {
		const options: Intl.DateTimeFormatOptions = {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		}
		return this.formatDate(new Date(datetime), options)
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * calendar.formatDateOnlyFromDate(new Date('2026-06-18')) // → '06/18/2026'
	 */
	formatDateOnlyFromDate(datetime: Date): string {
		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}
		return this.formatDate(datetime, options)
	}

	/**
	 * @example
	 * calendar.formatTimeFromDate(new Date('2026-06-18T10:30')) // → '10:30'
	 */
	formatTimeFromDate(datetime: Date): string {
		const options: Intl.DateTimeFormatOptions = {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		}
		return datetime.toLocaleTimeString(this.locale, options)
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * calendar.formatToShortDate('2026-06-18') // → 'thu, 18 jun '
	 * calendar.formatToShortDate('2026-06-18', true, '.', true) // → 'Thu. 18 jun 2026'
	 */
	formatToShortDate(
		date: string | Date,
		showYear: boolean = false,
		separator: string = ',',
		firstCharUpperCase: boolean = false
	): string {
		const formatter = new Intl.DateTimeFormat(this.locale, {
			weekday: 'short',
			day: '2-digit',
			month: 'short',
		})
		const theDate = new Date(date)
		const parts = formatter.formatToParts(theDate)!
		let weekday = this.retrieveDatePartByType(parts, 'weekday')
		if (firstCharUpperCase) weekday = String(weekday[0]).toUpperCase() + String(weekday).slice(1)
		const day = this.retrieveDatePartByType(parts, 'day')
		const month = this.retrieveDatePartByType(parts, 'month')
		const year = theDate.getFullYear()
		return `${weekday}${separator} ${day} ${month} ${showYear ? year : ''}`
	}

	/**
	 * @example
	 * 
	parts: 
		DateTimeFormatPart = {
			type: DateTimeFormatPartTypes;
			value: string;
		}
		[{ "type": "month", "value": "June" },
		{ "type": "literal", "value": " " },
		{ "type": "day", "value": "22" },]

	 * this.retrieveDatePartByType(parts, 'month') // → 'jun'
	 */

	private retrieveDatePartByType(parts: Record<string, any>[], type: string): string {
		return parts.find((part: any) => part.type === type)!.value.toLowerCase()
	}

	/**
	 * @example
	 * calendar.showDay('2026-06-18') // → '18'
	 */
	showDay(datetime: string | Date): string {
		const options: Intl.DateTimeFormatOptions = {
			day: '2-digit',
		}
		return this.formatDate(new Date(datetime), options)
	}

	/**
	 * @example
	 * // locale 'en-US'
	 * calendar.showMonth('2026-06-18') // → 'Jun'
	 */
	showMonth(datetime: string | Date): string {
		const options: Intl.DateTimeFormatOptions = {
			month: 'short',
		}
		return this.formatDate(new Date(datetime), options)
	}

	/**
	 * @example
	 * Calendar.getDateString(new Date(2026, 5, 8)) // → '2026-6-8'
	 */
	static getDateString(date: Date): string {
		const year = date.getFullYear()
		const month = date.getMonth() + 1
		const day = date.getDate()
		return `${year}-${month}-${day}`
	}

	/**
	 * @example
	 * Calendar.getDateStringTwoDigit(new Date(2026, 5, 8)) // → '2026-06-08'
	 */
	static getDateStringTwoDigit(date: Date): string {
		const year = date.getFullYear()
		const month = (date.getMonth() + 1).toString().padStart(2, '0')
		const day = date.getDate().toString().padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	/**
	 * @example
	 * Calendar.getTimeStringTwoDigit(new Date('2026-06-18T09:05:03')) // → '09:05:03'
	 */
	static getTimeStringTwoDigit(date: Date): string {
		const hour = date.getHours().toString().padStart(2, '0')
		const minutes = date.getMinutes().toString().padStart(2, '0')
		const seconds = date.getSeconds().toString().padStart(2, '0')
		return `${hour}:${minutes}:${seconds}`
	}

	/**
	 * @example
	 * Calendar.getDateTimeString(new Date('2026-06-18T09:05:03')) // → '2026-06-18T09:05:03'
	 */
	static getDateTimeString(date: Date): string {
		const dateString = this.getDateStringTwoDigit(date)
		const timeString = this.getTimeStringTwoDigit(date)
		return `${dateString}T${timeString}`
	}

	/**
	 * @example
	 * Calendar.getFirstDateOf('2026') // → '2026-01-01'
	 */
	static getFirstDateOf(year: string): string {
		return `${year}-01-01`
	}

	/**
	 * @example
	 * Calendar.getYear('2026-06-18') // → '2026'
	 */
	static getYear(date: string): string {
		const convertToDate = new Date(date)
		const year = convertToDate.getFullYear()
		return year.toString()
	}

	/**
	 * @example
	 * Calendar.getDateUTC('2026-06-18') // → Date for day 18, month 5 (June), year 2026
	 */
	static getDateUTC(date: string): Date {
		const splittedDate: string[] = date.split('-')
		const formattedDate: Date = new Date()
		const day: number = Number(splittedDate[2])
		const month: number = Number(splittedDate[1]) - 1
		const year: number = Number(splittedDate[0])
		formattedDate.setDate(day)
		formattedDate.setMonth(month)
		formattedDate.setFullYear(year)
		return formattedDate
	}

	/**
	 * @example
	 * // range is inclusive
	 * Calendar.countDays('2026-06-18', '2026-06-20') // → 3
	 */
	static countDays(startDate: string | Date, endDate: string | Date): number {
		const start = new Date(startDate)
		const end = new Date(endDate)
		const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24

		const milliseconds = end.getTime() - start.getTime()

		return milliseconds / MILLISECONDS_PER_DAY + 1
	}

	/**
	 * @example
	 * Calendar.getTimeDiffFromDates(
	 *   new Date('2026-06-18T10:00:00'),
	 *   new Date('2026-06-20T12:30:00')
	 * ) // → { days: 2, hours: 2, minutes: 30, seconds: 0 }
	 */
	static getTimeDiffFromDates(fromDate: Date, toDate: Date): Record<string, number> {
		const differenceInMilliSeconds = Math.abs(toDate.getTime() - fromDate.getTime())
		const msPerSecond = 1000
		const msPerMinute = 60 * msPerSecond
		const msPerHour = 60 * msPerMinute
		const msPerDay = 24 * msPerHour

		const days = Math.floor(differenceInMilliSeconds / msPerDay)
		let remainingMilliSeconds = differenceInMilliSeconds % msPerDay

		const hours = Math.floor(remainingMilliSeconds / msPerHour)
		remainingMilliSeconds = remainingMilliSeconds % msPerHour

		const minutes = Math.floor(remainingMilliSeconds / msPerMinute)
		remainingMilliSeconds = remainingMilliSeconds % msPerMinute

		const seconds = Math.floor(remainingMilliSeconds / msPerSecond)

		return {
			days,
			hours,
			minutes,
			seconds,
		}
	}
}
